import { WarningCircleIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isUnlimitedBudget, questionParams, type ChatMessage } from '@entities/search-session';
import { DISPLAY_TIME_ZONE } from '@entities/vehicle/lib';
import { Avatar } from '@shared/ui';

import styles from './Chat.module.css';

interface ChatMessageItemProps {
  message: ChatMessage;
  /**
   * Узкая лента рельса: аватар Karl уходит внутрь пузыря, рядом с именем и
   * маркировкой, — снаружи он съедал бы треть ширины реплики.
   */
  compact?: boolean | undefined;
}

/**
 * Время реплики: `HH:MM` в Europe/Berlin, формат — локали пользователя.
 * Битая дата не показывается вовсе, а не превращается в «Invalid Date».
 */
function formatMessageTime(isoDateTime: string, locale: string): string | null {
  const value = new Date(isoDateTime);
  if (Number.isNaN(value.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: DISPLAY_TIME_ZONE,
  }).format(value);
}

/** Текст реплики и время в одной строке, пока помещаются; длинный текст — над временем. */
function BubbleBody({ text, createdAt }: { text: ReactNode; createdAt?: string | undefined }) {
  const { i18n } = useTranslation();
  const time = createdAt ? formatMessageTime(createdAt, i18n.resolvedLanguage ?? 'de') : null;

  return (
    <div className={styles.body}>
      <p className={styles.text}>{text}</p>
      {time !== null ? (
        <time className={`${styles.time} tabular`} dateTime={createdAt}>
          {time}
        </time>
      ) : null}
    </div>
  );
}

interface KarlTurnProps {
  compact?: boolean | undefined;
  children: ReactNode;
}

/**
 * Реплика Karl: аватар со свечением, имя и маркировка «KI-Assistent»
 * (DESIGN_SYSTEM §5.4). Маркировка не декоративна — фотореалистичное лицо без
 * неё читается как живой человек (MVP_PRD §26). В широкой ленте она капсулой
 * стоит рядом с именем, в узкой ленте рельса — строкой под ним.
 */
export function KarlTurn({ compact = false, children }: KarlTurnProps) {
  const { t } = useTranslation();

  return (
    <li className={styles.turnAssistant}>
      {compact ? null : <Avatar variant="karl" size="md" />}
      <div className={styles.bubbleAssistant}>
        {compact ? (
          <p className={styles.authorCompact}>
            <Avatar variant="karl" size="sm" />
            <span className={styles.authorName}>{t('chat.assistant.name')}</span>
            <span className={styles.authorRole}>{t('chat.assistant.badge')}</span>
          </p>
        ) : (
          <p className={styles.author}>
            <span className={styles.authorName}>{t('chat.assistant.name')}</span>
            <span className={styles.authorTag}>{t('chat.assistant.badge')}</span>
          </p>
        )}
        {children}
      </div>
    </li>
  );
}

/**
 * Одна реплика диалога (MVP_PRD §25).
 *
 * Реплики пользователя и консультанта различаются не только цветом: у
 * ассистента есть аватар, имя и явная пометка «KI-Assistent». Karl —
 * не сотрудник, и интерфейс не имеет права создавать такое впечатление.
 *
 * Текст реплики консультанта приходит двумя способами и порядок между ними
 * жёсткий (FE-380, MVP_TECH_RFC §9.2):
 *
 * 1. `rendered_text` — реплика, сформулированная разговорным слоем и прошедшая
 *    детерминированные проверки backend. Печатается как есть: переводить её
 *    нечего, она уже на языке пользователя;
 * 2. `content` — ключ i18n (`chat.questions.budget_mode`), прежний
 *    фиксированный текст. Он же fallback: показывается всегда, когда
 *    `rendered_text` пуст — контур выключен, модель не ответила или ответ
 *    отбракован. **Отличать эти случаи на экране нельзя**: fallback выглядит
 *    как обычная реплика, «ошибки генерации» пользователь не видит.
 *
 * Числа в параметрах фиксированного текста приходят в центах и форматируются
 * валютой локали. Сгенерированной реплике параметры не нужны: все числа в ней
 * уже проверены guardrail'ом «только из состояния».
 *
 * Список изменённых критериев в ленте не показывается: разбор «какое поле и как
 * поменялось» — это отладочный взгляд на документ, а не разговор. Пользователю
 * достаточно панели критериев и одного показателя полноты запроса.
 */
export function ChatMessageItem({ message, compact = false }: ChatMessageItemProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  if (message.role === 'user') {
    const failed = message.status === 'failed';
    return (
      <li className={styles.turnUser}>
        <div
          className={[styles.bubbleUser, failed && styles.bubbleFailed].filter(Boolean).join(' ')}
        >
          <BubbleBody text={message.content} createdAt={message.created_at} />
        </div>
        {failed ? (
          <p className={styles.messageNote}>
            <WarningCircleIcon size={15} weight="fill" aria-hidden="true" />
            {t('chat.message.failed')}
          </p>
        ) : null}
      </li>
    );
  }

  // Сформулированная реплика печатается как есть — она уже на языке
  // пользователя и уже проверена backend (BE-382).
  const rendered = message.rendered_text?.trim();

  // Ключа может не быть в словаре, если backend добавил вопрос раньше фронта.
  // Показывать сырой `chat.questions.*` пользователю нельзя — тогда берётся
  // нейтральное подтверждение, которое всегда верно по смыслу.
  const baseKey = i18n.exists(message.content) ? message.content : 'chat.messages.acknowledged';
  const rawParams = message.metadata.params ?? {};
  const budgetCents = rawParams['budget_cents'];
  // Вопрос про бюджет построен вокруг суммы, а «без верхней границы» — не
  // сумма: подстановка ломала бы фразу. Для таких случаев есть вариант текста
  // без числа; если его нет — используется обычный.
  const unlimitedKey = `${baseKey}_unlimited`;
  const key =
    typeof budgetCents === 'number' && isUnlimitedBudget(budgetCents) && i18n.exists(unlimitedKey)
      ? unlimitedKey
      : baseKey;
  const params = questionParams(rawParams, locale, t('criteria.value.unlimitedBudget'));

  return (
    <KarlTurn compact={compact}>
      <BubbleBody text={rendered || t(key, params)} createdAt={message.created_at} />
    </KarlTurn>
  );
}

/** Приветствие пустого диалога: та же реплика Karl, только без времени. */
export function KarlGreeting({ compact = false }: { compact?: boolean | undefined }) {
  const { t } = useTranslation();
  return (
    <KarlTurn compact={compact}>
      <BubbleBody text={t('chat.intro.greeting')} />
    </KarlTurn>
  );
}
